import { useContext, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { deriveExtractionSuggestion } from './TextField/deriveExtractionSuggestion';
import {
  getDE2SuggestionStatusStorageKey,
  readSuggestionStatusFromBucket,
  SuggestionStatusBucket,
} from 'concepts/AutoDocExtraction/suggestionStatusStorage';
import {
  useAutoDocExtSuggestionContext,
  getSingleExtraction,
} from 'concepts/AutoDocExtraction/AutoDocExtSuggestionProvider';
import { DocumentViewerContext } from 'contexts/DocumentViewerProvider';
import { useDE2TrackingContext } from 'concepts/Extraction/Extract/DE2TrackingContext';
import { trackFieldRendered } from 'concepts/Extraction/Extract/tracking';
import usePersistedState from 'hooks/usePersistedState';
import { getReviewId } from 'query/review';

export const useTrackDE2BlockRendered = ({
  autoDocExtField,
  formKey,
  fieldAlias,
  fieldType,
}: {
  autoDocExtField: string;
  formKey: string;
  fieldAlias: string;
  fieldType: string;
}) => {
  const trackedDocumentIds = useRef(new Set<number>());
  const trackingContext = useDE2TrackingContext();
  // Defensive read: hook may be called outside DocumentViewerProvider (e.g. in tests)
  const documentViewerContext = useContext(DocumentViewerContext);
  const { getValues } = useFormContext();
  const reviewId = getReviewId();

  const studyId = trackingContext?.studyId;
  const useSuggestions = trackingContext?.useSuggestions ?? false;
  // Default true so that absent context holds the effect until context is present
  const featuresLoading = trackingContext?.featuresLoading ?? true;
  const document = documentViewerContext?.selectedDocument;
  const documentId = document?.id ?? undefined;
  const documentExtractable = document?.extractable;

  // reviewId is null only in navigation states where the effect below bails
  // out, so the key this yields is never actually read.
  const storageKey = getDE2SuggestionStatusStorageKey({
    reviewId: reviewId ?? 'no-review',
    documentId,
  });

  const [statusBucket] = usePersistedState<SuggestionStatusBucket>(
    storageKey,
    {}
  );

  const { allSuggestions } = useAutoDocExtSuggestionContext();
  const { isInitialLoading } = allSuggestions;
  const extraction = getSingleExtraction(allSuggestions, autoDocExtField);

  useEffect(() => {
    // Defer tracking until a document is selected — events without a document
    // pollute the denominator with false has_suggestion: false readings
    if (!documentId || !documentExtractable) return;
    // Wait for the review features fetch so useSuggestions is settled
    if (featuresLoading) return;
    // Skip if outside the DE2TrackingContext to avoid malformed telemetry
    if (!studyId) return;
    // Skip if the review ID is unavailable (e.g. unexpected navigation state)
    if (!reviewId) return;
    if (isInitialLoading) return;
    if (trackedDocumentIds.current.has(documentId)) return;

    const { suggestion, has_suggestion } = deriveExtractionSuggestion(
      extraction
    );
    const content = getValues(formKey)?.value;

    trackFieldRendered({
      field: autoDocExtField,
      field_alias: fieldAlias,
      field_type: fieldType,
      review_id: reviewId,
      study_id: studyId,
      document_id: documentId,
      suggestion,
      has_suggestion,
      has_content: content !== undefined && content !== null,
      suggestions_enabled: useSuggestions,
      suggestion_rejected:
        readSuggestionStatusFromBucket(statusBucket, formKey) === 'rejected',
    });

    trackedDocumentIds.current.add(documentId);
  }, [
    documentId,
    featuresLoading,
    isInitialLoading,
    extraction,
    autoDocExtField,
    reviewId,
    studyId,
    useSuggestions,
    formKey,
    fieldAlias,
    fieldType,
    getValues,
    statusBucket,
  ]);
};

// Null-render component so the React Query dependency is only introduced
// into the tree when the block is actually suggestible (autoDocExtField present).
export function DE2FieldRenderTracker({
  autoDocExtField,
  formKey,
  fieldAlias,
  fieldType,
}: {
  autoDocExtField: string;
  formKey: string;
  fieldAlias: string;
  fieldType: string;
}): null {
  useTrackDE2BlockRendered({ autoDocExtField, formKey, fieldAlias, fieldType });
  return null;
}
